import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { ImmotionAuftragSection } from "../../../features/work-order";
import {
  useWorkOrderDetail,
  getWorkOrderStatusLabel,
  getWorkOrderStatusColor,
  isFollowUpRequired,
  isNewAppointmentAllowed,
} from "../../../entities/work-order";
import { routes } from "../../../shared/config/routes";
import { AppointmentsSection } from "./ui/AppointmentsSection";
import { BadgeCell } from "./ui/BadgeCell";
import { AuditLogFeed } from "../../../widgets/audit-log";
import { UpsertAppointmentModal, useUpsertAppointment } from "../../../features/appointment-upsert";
import { useAuthStore } from "../../../features/auth/model/store";
import { useIsMobile } from "../../../shared/hooks/useIsMobile";
import { hasAnyRole } from "@myapp/contract";

const ADMIN_ROLES: import('@myapp/contract').UserRole[] = ['MANAGER', 'DIRECTOR', 'SUPERUSER'];

export const WorkOrderDetailPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { data, isLoading, isError } = useWorkOrderDetail(workOrderId);
  const { openCreate, openEdit } = useUpsertAppointment();

  const handleAddAppointment = () => {
    if (!workOrderId) return;
    openCreate(workOrderId);
  };

  // The same modal, reached the other way round: pick the slot on the board first, with the whole
  // day's workload visible, and let the form open already filled in.
  const handlePlanOnBoard = () => {
    if (!workOrderId) return;
    navigate(routes.disposition.planAppointment(workOrderId));
  };

  const isFollowUp = data ? isFollowUpRequired(data.status) : false;
  const canAddAppointment = data ? isNewAppointmentAllowed(data) : false;

  // A Folgetermin is just another appointment on the same work order — same modal, different
  // button label. Only the gating above differs.
  const handlePlanFollowUp = handleAddAppointment;

  const addAppointmentLabel = isFollowUp ? "Folgetermin planen" : "Neuer Termin";
  const showActions = Boolean(data && canAddAppointment);

  const pageTitle = (
    <>
      Auftrag <em style={{ fontWeight: 400 }}>Nr. {workOrderId}</em>
      {data &&
        // Spelled out at heading size the status doubles the length of the title, which on a phone
        // costs a whole line. A badge says the same thing at a size the heading can carry.
        (isMobile ? (
          <span className="ml-2 align-middle">
            <BadgeCell
              color={getWorkOrderStatusColor(data.status)}
              label={getWorkOrderStatusLabel(data.status)}
            />
          </span>
        ) : (
          <span style={{ color: getWorkOrderStatusColor(data.status) }}>
            {" "}{getWorkOrderStatusLabel(data.status)}
          </span>
        ))}
    </>
  );

  return (
    <PageLayout
      pageTitle={pageTitle}
      scrollable
      back
      backTo={routes.task.root}
      // Two actions, both with sentence-long labels: beside the title they leave it no room, so
      // below 768 px they take a row of their own. They stay in the header either way — pinned
      // above the content rather than scrolling away with it.
      stackActionsOnMobile={showActions}
      primaryAction={showActions ? {
        label: addAppointmentLabel,
        onClick: isFollowUp ? handlePlanFollowUp : handleAddAppointment,
      } : undefined}
      secondaryAction={showActions ? {
        label: "Auf Kalender planen",
        onClick: handlePlanOnBoard,
      } : undefined}
    >
      {isLoading && (
        <p className="mt-6 text-gray-400">Lade...</p>
      )}

      {isError && (
        <p className="mt-6 text-red-500">Auftrag konnte nicht geladen werden.</p>
      )}

      {data && (
        <div className="flex flex-col gap-4 md:gap-[20px] mt-4 md:mt-6 overflow-auto">
          <ImmotionAuftragSection
            immotionAuftragId={workOrderId ?? ""}
            readonly
          />
          <AppointmentsSection appointments={data.appointments} onEdit={openEdit} />
          {user && hasAnyRole(user.roles ?? [], ADMIN_ROLES) && workOrderId && (
            <AuditLogFeed workOrderId={workOrderId} />
          )}
        </div>
      )}

      <UpsertAppointmentModal
        workOrderId={workOrderId}
        defaultTradeCode={data?.external.gewerkId ?? 0}
      />
    </PageLayout>
  );
};
